import { usePathname, useRouter } from 'next/navigation';
import { useMemo } from 'react';

import { type Locale } from '@/utils/i18n-config';

export function useLanguage() {
  const pathname = usePathname();
  const router = useRouter();

  // 从路径中获取当前语言
  const currentLanguage = useMemo((): Locale => {
    if (pathname.startsWith('/zh/')) {
      return 'zh';
    }
    return 'en';
  }, [pathname]);

  // 获取语言显示名称
  const getLanguageDisplayName = (lang: Locale): string => {
    return lang === 'zh' ? '中文' : 'English';
  };

  // 切换语言
  const switchLanguage = (newLang: Locale) => {
    if (currentLanguage === newLang) {
      return;
    }

    let newPath = pathname;
    if (currentLanguage === 'zh') {
      // 从中文切换到英文：移除 /zh/ 前缀
      newPath = pathname.replace('/zh/', '/');
    } else {
      // 从英文切换到中文：添加 /zh/ 前缀
      newPath = pathname.replace('/', `/${newLang}/`);
    }

    // 保留查询参数
    if (typeof window !== 'undefined') {
      const queryString = window.location.search;
      if (queryString) {
        newPath += queryString;
      }
    }

    router.prefetch(newPath);
    router.replace(newPath);
  };

  // 获取当前路径（不包含语言前缀）
  const getPathWithoutLanguage = (): string => {
    if (currentLanguage === 'zh') {
      return pathname.replace('/zh', '');
    }
    return pathname;
  };

  // 检查是否为指定语言
  const isLanguage = (lang: Locale): boolean => {
    return currentLanguage === lang;
  };

  return {
    currentLanguage,
    getLanguageDisplayName,
    switchLanguage,
    getPathWithoutLanguage,
    isLanguage,
  };
}
